import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../../shared/components/Table/Table";
import Modal from "../../../shared/components/Modal";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import ResumeSearchModal from "../components/ResumeSearchModal";
import { CustomDropdown } from "../../../shared/components/CustomDropdown";
import strings from "../../../utils/Strings";
import { Learner } from "../data/interfaces/LearnerInterfaces";
import { useLearnerContext } from "../data/context/LearnerContext";
import LearnerClient from "../data/api/LearnerClient";
import { useAuthContext } from "../../auth/data/context/AuthContext";
import { getResumePreviewUrl, getResumeDownloadUrl } from "../../../services/resumeFileService";
import ResumeViewer, { ResumeZoomControls, ResumeViewerHandle } from "../../../shared/components/ResumeViewer";
import "./../styles/LearnerStyles.css";

import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";

const BATCH_SIZE = 25;

const SkillsCell: React.FC<{ skills?: string[], onShowAll: (title: string, items: string[]) => void }> = ({ skills = [], onShowAll }) => {
  if (!skills || skills.length === 0) return <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>—</span>;

  let displayLimit = skills.length;
  let charCount = 0;
  for (let i = 0; i < skills.length; i++) {
    charCount += skills[i].length + 6;
    if (charCount > 45) {
      displayLimit = Math.max(1, i);
      break;
    }
  }

  const displaySkills = skills.slice(0, displayLimit);
  const hiddenCount = skills.length - displayLimit;
  const chipBg = "#e0f2fe";
  const chipColor = "#0284c7";

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", maxWidth: "250px" }}>
      {displaySkills.map((s, i) => (
        <span key={i} style={{ padding: "4px 10px", background: chipBg, color: chipColor, borderRadius: "16px", fontSize: "0.75rem", whiteSpace: "nowrap", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", maxWidth: "140px" }}>
          {s}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          onClick={(e) => { e.stopPropagation(); onShowAll("Skills", skills); }}
          className="custom-tooltip"
          data-tooltip="Click to view all"
          style={{ padding: "4px 10px", background: "#e0e7ff", color: "#4f46e5", borderRadius: "16px", fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap", fontWeight: 600 }}
        >
          +{hiddenCount} Show all
        </span>
      )}
    </div>
  );
};

const JobCategoriesCell: React.FC<{ job_categories?: string[], onShowAll: (title: string, items: string[]) => void }> = ({ job_categories = [], onShowAll }) => {
  if (!job_categories || job_categories.length === 0) return <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>—</span>;

  let displayLimit = job_categories.length;
  let charCount = 0;
  for (let i = 0; i < job_categories.length; i++) {
    charCount += job_categories[i].length + 6;
    if (charCount > 55) {
      displayLimit = Math.max(1, i);
      break;
    }
  }

  const displayItems = job_categories.slice(0, displayLimit);
  const hiddenCount = job_categories.length - displayLimit;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", maxWidth: "260px" }}>
      {displayItems.map((s, i) => (
        <span key={i} style={{ padding: "4px 10px", background: "#fce7f3", color: "#be185d", borderRadius: "16px", fontSize: "0.75rem", whiteSpace: "nowrap", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", maxWidth: "140px" }}>
          {s}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          onClick={(e) => { e.stopPropagation(); onShowAll("Job Categories", job_categories); }}
          className="custom-tooltip"
          data-tooltip="Click to view all"
          style={{ padding: "4px 10px", background: "#fbcfe8", color: "#9d174d", borderRadius: "16px", fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap", fontWeight: 600 }}
        >
          +{hiddenCount} Show all
        </span>
      )}
    </div>
  );
};

const CertificationsCell: React.FC<{ certifications?: string[], onShowAll: (title: string, items: string[]) => void }> = ({ certifications = [], onShowAll }) => {
  if (!certifications || certifications.length === 0) return <span style={{ color: "#94a3b8", fontSize: "0.85rem" }}>—</span>;

  let displayLimit = certifications.length;
  let charCount = 0;
  for (let i = 0; i < certifications.length; i++) {
    charCount += certifications[i].length + 6;
    if (charCount > 45) {
      displayLimit = Math.max(1, i);
      break;
    }
  }

  const displayItems = certifications.slice(0, displayLimit);
  const hiddenCount = certifications.length - displayLimit;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center", maxWidth: "250px" }}>
      {displayItems.map((s, i) => (
        <span key={i} style={{ padding: "4px 10px", background: "#f3e8ff", color: "#7e22ce", borderRadius: "16px", fontSize: "0.75rem", whiteSpace: "nowrap", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", maxWidth: "140px" }}>
          {s}
        </span>
      ))}
      {hiddenCount > 0 && (
        <span
          onClick={(e) => { e.stopPropagation(); onShowAll("Professional Certifications", certifications); }}
          className="custom-tooltip"
          data-tooltip="Click to view all"
          style={{ padding: "4px 10px", background: "#e9d5ff", color: "#6b21a8", borderRadius: "16px", fontSize: "0.75rem", cursor: "pointer", whiteSpace: "nowrap", fontWeight: 600 }}
        >
          +{hiddenCount} Show all
        </span>
      )}
    </div>
  );
};

const LearnerListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState } = useAuthContext();
  const { state, dispatch } = useLearnerContext();
  const [search, setSearch] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [tempSelectedSkills, setTempSelectedSkills] = useState<string[]>([]);
  const [skillSearchText, setSkillSearchText] = useState("");
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [showSelectedSkillsTooltip, setShowSelectedSkillsTooltip] = useState(false);
  const skillDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedJobCategories, setSelectedJobCategories] = useState<string[]>([]);
  const [tempSelectedJobCategories, setTempSelectedJobCategories] = useState<string[]>([]);
  const [jobCategorySearchText, setJobCategorySearchText] = useState("");
  const [showJobCategoryDropdown, setShowJobCategoryDropdown] = useState(false);
  const [showSelectedJobCategoriesTooltip, setShowSelectedJobCategoriesTooltip] = useState(false);
  const jobCategoryDropdownRef = useRef<HTMLDivElement>(null);
  const [deleteModal, setDeleteModal] = useState<Learner | null>(null);
  const [noteModal, setNoteModal] = useState<string | null>(null);
  const [skillsModal, setSkillsModal] = useState<{ title: string, items: string[] } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const isFetchingRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  const MAX_BULK_APPROVE = 5;

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const [approveModal, setApproveModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [onboardModal, setOnboardModal] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [infoTitle, setInfoTitle] = useState<string>("Info");
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string>("Error");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchLearners = useCallback(
    async (reset = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (reset) dispatch({ type: "SET_LOADING", payload: true });
      else setLoadingMore(true);
      try {
        const params: any = {
          page_number: reset ? 1 : page,
          limit: BATCH_SIZE,
        };
        if (search) params.name = search;
        if (selectedSkills.length > 0) params.skills = selectedSkills.join(",");
        if (selectedJobCategories.length > 0) params.job_categories = selectedJobCategories.join(",");
        if (statusFilter !== "all") params.status = statusFilter;
        const data = await LearnerClient.list(params);
        const records: Learner[] = data.records || [];
        if (reset) {
          dispatch({
            type: "SET_LEARNERS",
            payload: { records, total: data.total_filtered_records ?? (data.total_no_of_records || 0) },
          });
          setPage(2);
        } else {
          dispatch({
            type: "SET_LEARNERS",
            payload: {
              records: [...state.learners, ...records],
              total: data.total_filtered_records ?? (data.total_no_of_records || 0),
            },
          });
          setPage((p) => p + 1);
        }
        setHasMore(records.length === BATCH_SIZE);
      } catch (err: any) {
        dispatch({ type: "SET_ERROR", payload: err.message });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
        setLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [search, selectedSkills, selectedJobCategories, statusFilter, page, dispatch, state.learners],
  );

  useEffect(() => {
    fetchLearners(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedSkills, selectedJobCategories, statusFilter]);

  // Infinite scroll listener
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (
        el.scrollTop + el.clientHeight >= el.scrollHeight - 80 &&
        hasMore &&
        !loadingMore
      ) {
        fetchLearners(false);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, fetchLearners]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportDropdown(false);
      }
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(e.target as Node)) {
        setShowSkillDropdown(false);
        setShowSelectedSkillsTooltip(false);
      }
      if (jobCategoryDropdownRef.current && !jobCategoryDropdownRef.current.contains(e.target as Node)) {
        setShowJobCategoryDropdown(false);
        setShowSelectedJobCategoriesTooltip(false);
      }
    };
    if (showExportDropdown || showSkillDropdown || showSelectedSkillsTooltip || showJobCategoryDropdown || showSelectedJobCategoriesTooltip) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showExportDropdown, showSkillDropdown, showSelectedSkillsTooltip, showJobCategoryDropdown, showSelectedJobCategoriesTooltip]);

  useEffect(() => {
    if (showSkillDropdown) {
      setTempSelectedSkills(selectedSkills);
      setSkillSearchText("");
    }
  }, [showSkillDropdown, selectedSkills]);

  useEffect(() => {
    if (showJobCategoryDropdown) {
      setTempSelectedJobCategories(selectedJobCategories);
      setJobCategorySearchText("");
    }
  }, [showJobCategoryDropdown, selectedJobCategories]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEdit = (row: Learner) =>
    navigate(`/dashboard/learners/edit`, {
      state: { email: row.email_address },
    });

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await LearnerClient.delete(deleteModal.email_address);
      dispatch({ type: "DELETE_LEARNER", payload: deleteModal.email_address });
      setDeleteModal(null);
    } catch (err: any) {
      dispatch({ type: "SET_ERROR", payload: err.message });
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleting(true);
    try {
      const selectedEmails = Array.from(selectedRows);
      await LearnerClient.bulkDeleteUsers(selectedEmails);

      // We manually update local state to hide them or refetch
      selectedEmails.forEach(email => {
        dispatch({ type: "DELETE_LEARNER", payload: email });
      });
      setSelectedRows(new Set());
      setInfoTitle("Success");
      setInfoMessage(`Successfully deleted ${selectedEmails.length} applicant${selectedEmails.length === 1 ? '' : 's'}.`);
      setBulkDeleteModal(false);
    } catch (err: any) {
      dispatch({ type: "SET_ERROR", payload: err.message });
    } finally {
      setBulkDeleting(false);
    }
  };



  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getBadgeClass = (status: string) => {
    const s = (status || "").toLowerCase();
    if (s === "active" || s === "interviewed" || s === "approved") return "lp-badge active";
    if (s === "draft" || s === "pending") return "lp-badge pending";
    if (s === "review") return "lp-badge review";
    if (s === "inactive" || s === "failed") return "lp-badge inactive";
    return "lp-badge pending";
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(state.learners.map((l) => l.email_address)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (email: string, checked: boolean) => {
    const newSet = new Set(selectedRows);
    if (checked) newSet.add(email);
    else newSet.delete(email);
    setSelectedRows(newSet);
  };

  const [isExporting, setIsExporting] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [resumeSearchOpen, setResumeSearchOpen] = useState(
    location.state?.openCandidateSearch === true
  );

  useEffect(() => {
    if (location.state?.openCandidateSearch) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const [resumePreviewUrl, setResumePreviewUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [resumeZoom, setResumeZoom] = useState(1.5);
  const resumeViewerRef = useRef<ResumeViewerHandle>(null);

  const openResumePreview = async (url: string | null | undefined) => {
    setPdfLoading(true);
    const inlineUrl = await getResumePreviewUrl(url);
    setResumePreviewUrl(inlineUrl);
  };

  const handleExport = async (format: "csv" | "excel") => {
    const selectedLearners = state.learners.filter((l) =>
      selectedRows.has(l.email_address),
    );
    if (selectedLearners.length === 0) return;

    setIsExporting(true);
    setShowExportDropdown(false);
    try {
      const candidates = selectedLearners.map((l) => l.email_address);
      const res = await LearnerClient.exportUsers({
        file_type: format,
        candidates: candidates,
        export_all: false,
      });
      if (res && res.file_path) {
        window.open(res.file_path, "_blank");
      }
    } catch (err: any) {
      dispatch({ type: "SET_ERROR", payload: err.message || "Export failed" });
    } finally {
      setIsExporting(false);
    }
  };

  const handleApproveConfirm = async () => {
    const selectedLearners = state.learners.filter((l) =>
      selectedRows.has(l.email_address),
    );
    if (selectedLearners.length === 0) return;
    setApproving(true);
    try {
      const emails = selectedLearners.map((l) => l.email_address);
      const res = await LearnerClient.approveUsers(emails);
      const approved = (res?.results || []).filter(
        (r: any) => r.status === "approved",
      ).length;
      setApproveModal(false);
      setSelectedRows(new Set());
      setInfoTitle("Approval Successful");
      setInfoMessage(
        `${approved} applicant${approved === 1 ? '' : 's'} approved successfully.`,
      );
      await fetchLearners(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || err.message || "Approval failed";
      setErrorTitle("Approval Error");
      setErrorMsg(msg);
    } finally {
      setApproving(false);
    }
  };

  const handleOnboardConfirm = async () => {
    const selectedLearners = state.learners.filter((l) =>
      selectedRows.has(l.email_address) &&
      (l.status || "").toLowerCase() === "active" &&
      !l.onboarded
    );
    if (selectedLearners.length === 0) return;
    setOnboarding(true);
    try {
      const emails = selectedLearners.map((l) => l.email_address);
      const res = await LearnerClient.bulkOnboardUsers(emails);
      const onboarded = (res?.results || []).filter(
        (r: any) => r.status === "onboarded" || r.status === "already_onboarded",
      ).length;
      setOnboardModal(false);
      setSelectedRows(new Set());
      setInfoTitle("Onboarding Successful");
      setInfoMessage(
        `${onboarded} applicant${onboarded === 1 ? '' : 's'} onboarded successfully.`,
      );
      await fetchLearners(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || err.message || "Onboarding failed";
      setErrorTitle("Onboarding Error");
      setErrorMsg(msg);
    } finally {
      setOnboarding(false);
    }
  };

  const selectedDraftCount = state.learners.filter(
    (l) =>
      selectedRows.has(l.email_address) &&
      (l.status || "").toLowerCase() === "draft",
  ).length;

  const selectedOnboardableCount = state.learners.filter(
    (l) =>
      selectedRows.has(l.email_address) &&
      (l.status || "").toLowerCase() === "active" &&
      !l.onboarded
  ).length;

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setSelectedRows(new Set());
  };

  const handleRowApprove = (row: Learner) => {
    setSelectedRows(new Set([row.email_address]));
    setApproveModal(true);
  };

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader
        isLoading={state.loading || deleting || isExporting}
        message={
          deleting
            ? "Deleting applicants..."
            : isExporting
              ? "Exporting applicants..."
              : "Loading applicants..."
        }
      />

      {/* ── Hero ── */}
      <div className="lp-hero" style={{ flexDirection: "column", gap: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <div className="lp-hero-left">
            <h1>Applicants Directory</h1>
            <span className="lp-hero-chip">Manage, engage, and track all enrolled candidates in one place.</span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "12px 20px",
              gap: "16px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: "#f0fdf4",
                color: "#059669",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <span
                style={{
                  fontSize: "1rem",
                  color: "#64748b",
                  fontWeight: 600,
                  textTransform: "none",
                  letterSpacing: "0.5px",
                }}
              >
                Total Applicants:
              </span>
              <span
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                {state.totalCount || 0}
              </span>
            </div>
          </div>

          <div
            className="lp-hero-right"
            style={{ gap: "12px", display: "flex", alignItems: "center" }}
          >
            {selectedRows.size > 0 && (
              <>
                <div style={{ position: "relative" }} ref={exportRef}>
                  <button
                    className="btn-export"
                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                  >
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Export ({selectedRows.size})
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ marginLeft: 2 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {showExportDropdown && (
                    <div
                      className="up-template-dropdown"
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        marginTop: 8,
                        zIndex: 10,
                        minWidth: 160,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        padding: "6px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                      }}
                    >
                      <button
                        onClick={() => handleExport("excel")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          width: "100%",
                          border: "none",
                          background: "transparent",
                          textAlign: "left",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          color: "#334155",
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="#16a34a"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Excel (.xls)
                      </button>
                      <button
                        onClick={() => handleExport("csv")}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "8px 12px",
                          width: "100%",
                          border: "none",
                          background: "transparent",
                          textAlign: "left",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          color: "#334155",
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="#0284c7"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        CSV (.csv)
                      </button>
                    </div>
                  )}
                </div>

                {selectedDraftCount > 0 && (
                  <button
                    className="btn-primary"
                    disabled={approving}
                    onClick={() => {
                      if (selectedDraftCount > MAX_BULK_APPROVE) {
                        setErrorTitle("Approval Error");
                        setErrorMsg(`You can approve a maximum of ${MAX_BULK_APPROVE} applicants at a time. Please reduce the selection and try again.`);
                        return;
                      }
                      setApproveModal(true);
                    }}
                    style={{
                      opacity: approving ? 0.6 : 1,
                      cursor: approving ? "not-allowed" : "pointer",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {approving
                      ? "Approving..."
                      : `Approve (${selectedDraftCount})`}
                  </button>
                )}

                {selectedOnboardableCount > 0 && (
                  <button
                    className="al-onboard-btn"
                    disabled={onboarding}
                    onClick={() => {
                      setOnboardModal(true);
                    }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '9px 20px',
                      background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                      color: '#fff', border: 'none', borderRadius: 'var(--radius-full)',
                      fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                      opacity: onboarding ? 0.6 : 1,
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!onboarding) {
                        (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(22, 163, 74, 0.3)';
                        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!onboarding) {
                        (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.2)';
                        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {onboarding ? (
                      <svg width="16" height="16" className="al-spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    ) : (
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                    )}
                    {onboarding
                      ? "Onboarding..."
                      : `Onboard (${selectedOnboardableCount})`}
                  </button>
                )}

                {selectedRows.size > 0 && (
                  <button
                    className="al-delete-btn"
                    disabled={bulkDeleting}
                    onClick={() => setBulkDeleteModal(true)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      padding: '9px 20px',
                      background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                      color: '#fff', border: 'none', borderRadius: 'var(--radius-full)',
                      fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                      opacity: bulkDeleting ? 0.6 : 1,
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!bulkDeleting) {
                        (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(220, 38, 38, 0.3)';
                        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!bulkDeleting) {
                        (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.2)';
                        (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {bulkDeleting ? (
                      <svg width="16" height="16" className="al-spinner" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    ) : (
                      <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    )}
                    {bulkDeleting
                      ? "Deleting..."
                      : `Delete (${selectedRows.size})`}
                  </button>
                )}
              </>
            )}


            <button
              className="al-add-more-btn"
              onClick={() => navigate("/dashboard/learners/upload")}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload Resume
            </button>
            <button
              className="btn-primary"
              onClick={() => navigate("/dashboard/learners/add")}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Applicant
            </button>
          </div>
        </div>
      </div>

      {/* ── Table Panel with Scroll Pagination ── */}
      <div className="lp-table-panel" style={{ marginTop: "16px" }}>
        <div
          className="lp-table-panel-head"
          style={{
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <h2
              style={{
                fontSize: "1.15rem",
                fontWeight: 800,
                color: "#0f172a",
                margin: 0,
                letterSpacing: "-0.3px",
              }}
            >
              All Applicants
            </h2>
            <button
              onClick={() => fetchLearners(true)}
              title="Refresh Applicants"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "32px",
                height: "32px",
                background: "transparent",
                border: "none",
                borderRadius: "50%",
                color: "#64748b",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e2e8f0";
                e.currentTarget.style.color = "#0f172a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "#64748b";
              }}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            {search && (
              <div
                style={{
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                  padding: "5px 14px",
                  borderRadius: "20px",
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "currentColor",
                  }}
                />
                {state.learners.length} Found
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ position: "relative", width: 130 }}>
              <CustomDropdown
                value={statusFilter}
                options={[
                  { value: "all", label: "All Status" },
                  { value: "Draft", label: "Draft" },
                  { value: "Approved", label: "Approved" },
                  { value: "Active", label: "Active" },
                  { value: "InActive", label: "InActive" },
                ]}
                onChange={handleStatusFilterChange}
              />
            </div>
            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px" }} ref={skillDropdownRef}>
              <button
                className="btn-export"
                onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                style={{ height: 40, background: "#fff", border: "1px solid #e2e8f0", color: "#334155", display: "flex", alignItems: "center", gap: "8px", padding: "0 16px", borderRadius: "20px" }}
              >
                Skills Filter {selectedSkills.length > 0 && `(${selectedSkills.length})`}
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {selectedSkills.length > 0 && (
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowSelectedSkillsTooltip(!showSelectedSkillsTooltip)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#059669",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0
                    }}
                    title="View Selected Skills"
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  {showSelectedSkillsTooltip && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        marginTop: "8px",
                        zIndex: 20,
                        width: "220px",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px"
                      }}
                    >
                      <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0f172a", marginBottom: "4px" }}>Selected Skills</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {selectedSkills.map(s => (
                          <span key={s} style={{ padding: "4px 8px", background: "#f1f5f9", color: "#475569", borderRadius: "12px", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showSkillDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 8,
                    zIndex: 10,
                    width: 250,
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ padding: "12px", borderBottom: "1px solid #e2e8f0" }}>
                    <input
                      type="text"
                      placeholder="Search skills..."
                      value={skillSearchText}
                      onChange={(e) => setSkillSearchText(e.target.value)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "0.85rem", outline: "none" }}
                    />
                  </div>
                  <div style={{ padding: "8px", maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {(!authState.user?.skills || authState.user.skills.length === 0) ? (
                      <div style={{ padding: '8px', fontSize: '0.85rem', color: '#64748b' }}>No skills found.</div>
                    ) : (
                      [...authState.user.skills]
                        .sort((a, b) => a.localeCompare(b))
                        .filter((s) => s.toLowerCase().includes(skillSearchText.toLowerCase()))
                        .map((skill) => (
                          <label
                            key={skill}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "6px 8px",
                              fontSize: "0.85rem",
                              color: "#334155",
                              cursor: "pointer",
                              borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <input
                              type="checkbox"
                              checked={tempSelectedSkills.includes(skill)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTempSelectedSkills([...tempSelectedSkills, skill]);
                                } else {
                                  setTempSelectedSkills(tempSelectedSkills.filter(s => s !== skill));
                                }
                              }}
                              style={{ accentColor: '#16a34a' }}
                            />
                            {skill}
                          </label>
                        ))
                    )}
                  </div>
                  <div style={{ padding: "12px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", gap: "8px", background: "#f8fafc", borderRadius: "0 0 10px 10px" }}>
                    <button
                      onClick={() => {
                        setTempSelectedSkills([]);
                        setSelectedSkills([]);
                        setShowSkillDropdown(false);
                      }}
                      style={{ flex: 1, padding: "8px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px", color: "#475569", fontSize: "0.85rem", cursor: "pointer", fontWeight: 600 }}
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSkills(tempSelectedSkills);
                        setShowSkillDropdown(false);
                      }}
                      disabled={tempSelectedSkills.length === 0 && selectedSkills.length === 0}
                      style={{ flex: 1, padding: "8px", background: (tempSelectedSkills.length === 0 && selectedSkills.length === 0) ? "#94a3b8" : "#059669", border: "none", borderRadius: "6px", color: "#fff", fontSize: "0.85rem", cursor: (tempSelectedSkills.length === 0 && selectedSkills.length === 0) ? "not-allowed" : "pointer", fontWeight: 600 }}
                    >
                      View
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px" }} ref={jobCategoryDropdownRef}>
              <button
                className="btn-export"
                onClick={() => setShowJobCategoryDropdown(!showJobCategoryDropdown)}
                style={{ height: 40, background: "#fff", border: "1px solid #e2e8f0", color: "#334155", display: "flex", alignItems: "center", gap: "8px", padding: "0 16px", borderRadius: "20px" }}
              >
                Job Categories Filter {selectedJobCategories.length > 0 && `(${selectedJobCategories.length})`}
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {selectedJobCategories.length > 0 && (
                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowSelectedJobCategoriesTooltip(!showSelectedJobCategoriesTooltip)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#059669",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0
                    }}
                    title="View Selected Job Categories"
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  {showSelectedJobCategoriesTooltip && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        marginTop: "8px",
                        zIndex: 20,
                        width: "220px",
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        padding: "12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px"
                      }}
                    >
                      <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#0f172a", marginBottom: "4px" }}>Selected Job Categories</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {selectedJobCategories.map(s => (
                          <span key={s} style={{ padding: "4px 8px", background: "#f1f5f9", color: "#475569", borderRadius: "12px", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showJobCategoryDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    marginTop: 8,
                    zIndex: 10,
                    width: 250,
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div style={{ padding: "12px", borderBottom: "1px solid #e2e8f0" }}>
                    <input
                      type="text"
                      placeholder="Search job categories..."
                      value={jobCategorySearchText}
                      onChange={(e) => setJobCategorySearchText(e.target.value)}
                      style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", fontSize: "0.85rem", outline: "none" }}
                    />
                  </div>
                  <div style={{ padding: "8px", maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
                    {(!authState.user?.job_categories || authState.user.job_categories.length === 0) ? (
                      <div style={{ padding: '8px', fontSize: '0.85rem', color: '#64748b' }}>No job categories found.</div>
                    ) : (
                      [...authState.user.job_categories]
                        .sort((a, b) => a.localeCompare(b))
                        .filter((s) => s.toLowerCase().includes(jobCategorySearchText.toLowerCase()))
                        .map((cat) => (
                          <label
                            key={cat}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              padding: "6px 8px",
                              fontSize: "0.85rem",
                              color: "#334155",
                              cursor: "pointer",
                              borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <input
                              type="checkbox"
                              checked={tempSelectedJobCategories.includes(cat)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setTempSelectedJobCategories([...tempSelectedJobCategories, cat]);
                                } else {
                                  setTempSelectedJobCategories(tempSelectedJobCategories.filter(s => s !== cat));
                                }
                              }}
                              style={{ accentColor: '#16a34a' }}
                            />
                            {cat}
                          </label>
                        ))
                    )}
                  </div>
                  <div style={{ padding: "12px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", gap: "8px", background: "#f8fafc", borderRadius: "0 0 10px 10px" }}>
                    <button
                      onClick={() => {
                        setTempSelectedJobCategories([]);
                        setSelectedJobCategories([]);
                        setShowJobCategoryDropdown(false);
                      }}
                      style={{ flex: 1, padding: "8px", background: "#fff", border: "1px solid #cbd5e1", borderRadius: "6px", color: "#475569", fontSize: "0.85rem", cursor: "pointer", fontWeight: 600 }}
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => {
                        setSelectedJobCategories(tempSelectedJobCategories);
                        setShowJobCategoryDropdown(false);
                      }}
                      disabled={tempSelectedJobCategories.length === 0 && selectedJobCategories.length === 0}
                      style={{ flex: 1, padding: "8px", background: (tempSelectedJobCategories.length === 0 && selectedJobCategories.length === 0) ? "#94a3b8" : "#059669", border: "none", borderRadius: "6px", color: "#fff", fontSize: "0.85rem", cursor: (tempSelectedJobCategories.length === 0 && selectedJobCategories.length === 0) ? "not-allowed" : "pointer", fontWeight: 600 }}
                    >
                      View
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              className="lp-search"
              style={{
                height: 40,
                width: 280,
                background: "#fff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                position: "relative",
              }}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="#94a3b8"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingRight: search ? "30px" : "10px" }}
              />
              {search && (
                <button
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "#f1f5f9",
                    cursor: "pointer",
                    color: "#64748b",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                  onClick={() => setSearch("")}
                  title="Clear Search"
                >
                  <svg
                    width="12"
                    height="12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>


          </div>
        </div>

        <div className="lp-table-scroll-area" ref={scrollRef}>
          <div className="lp-table-wrapper">
            {state.loading ? (
              <div className="table-loading">
                <div className="table-loading-spinner" />
                <span>Loading applicants...</span>
              </div>
            ) : state.learners.length === 0 ? (
              <div className="table-empty">
                <svg
                  width="40"
                  height="40"
                  fill="none"
                  stroke="#cbd5e1"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>No applicants found</span>
                {search && (
                  <span style={{ fontSize: "0.8rem" }}>
                    Try adjusting your search
                  </span>
                )}
              </div>
            ) : (
              <Table className="learner-table">
                <TableHeader>
                  <TableRow>
                    <TableHead style={{ width: 40, paddingLeft: 20 }}>
                      <input
                        type="checkbox"
                        className="lp-checkbox"
                        checked={
                          state.learners.length > 0 &&
                          selectedRows.size === state.learners.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableHead>
                    <TableHead>Applicant Info</TableHead>
                    <TableHead>Education</TableHead>
                    <TableHead>Certifications</TableHead>
                    <TableHead>Resume</TableHead>
                    <TableHead>Skills</TableHead>
                    <TableHead>Job Categories</TableHead>
                    <TableHead>Years of Experience</TableHead>
                    <TableHead className="actions-header">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.learners.map((row, i) => {
                    const status = (row.status || "").toLowerCase();
                    const isActive = status === "active" || status === "interviewed";
                    const isInactive = status === "inactive" || status === "failed";
                    const isDraft = status === "draft" || status === "action required";
                    const rowBg = isActive ? "rgba(34, 197, 94, 0.08)" : isInactive ? "rgba(148, 163, 184, 0.15)" : isDraft ? "rgba(234, 179, 8, 0.1)" : undefined;


                    return (
                      <TableRow
                        key={row.email_address || i}
                        data-state={
                          selectedRows.has(row.email_address)
                            ? "selected"
                            : undefined
                        }
                        style={{ height: "100px", backgroundColor: rowBg }}
                      >
                        <TableCell style={{ paddingLeft: 20 }}>
                          <input
                            type="checkbox"
                            className="lp-checkbox"
                            checked={selectedRows.has(row.email_address)}
                            onChange={(e) =>
                              handleSelectRow(row.email_address, e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div
                            className="lp-user-cell"
                            style={{ justifyContent: "flex-start", alignItems: "flex-start" }}
                          >
                            <div
                              className="lp-avatar"
                              style={{
                                marginTop: "4px",
                                background: isActive ? "linear-gradient(135deg, #22c55e, #15803d)" : isInactive ? "linear-gradient(135deg, #94a3b8, #475569)" : isDraft ? "linear-gradient(135deg, #facc15, #ca8a04)" : undefined,
                              }}
                            >
                              {(row.first_name?.[0] || "U").toUpperCase()}
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", maxWidth: "150px" }}>
                              <div className="lp-user-name" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span
                                  className={(`${row.first_name || ""} ${row.last_name || ""}`.length > 15) ? "custom-tooltip" : ""}
                                  data-tooltip={(`${row.first_name || ""} ${row.last_name || ""}`.length > 15) ? `${row.first_name} ${row.last_name}` : undefined}
                                  style={{ display: "inline-flex", minWidth: 0, flexShrink: 1 }}
                                >
                                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {row.first_name} {row.last_name}
                                  </span>
                                </span>
                                {row.note && (
                                  <svg
                                    onClick={() => setNoteModal(row.note as string)}
                                    className="custom-tooltip"
                                    data-tooltip="View Notes"
                                    style={{ cursor: "pointer", color: "#0ea5e9", flexShrink: 0 }}
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.2L4 17.2V4h16v12z" />
                                    <path d="M6 9h12v2H6zm0 3h8v2H6zm0-6h12v2H6z" />
                                  </svg>
                                )}
                              </div>
                              {row.email_address && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.email_address || "") && (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ color: "#94a3b8", flexShrink: 0 }}>
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                  </svg>
                                  <span
                                    className={(row.email_address && row.email_address.length > 15) ? "custom-tooltip" : ""}
                                    data-tooltip={(row.email_address && row.email_address.length > 15) ? row.email_address : undefined}
                                    style={{ display: "inline-flex", minWidth: 0, flex: 1 }}
                                  >
                                    <span style={{ color: "#64748b", fontSize: "0.80rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {row.email_address}
                                    </span>
                                  </span>
                                </div>
                              )}
                              {row.phone && (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{ color: "#94a3b8", flexShrink: 0 }}>
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                  </svg>
                                  <span
                                    className={(row.phone && row.phone.length > 15) ? "custom-tooltip" : ""}
                                    data-tooltip={(row.phone && row.phone.length > 15) ? row.phone : undefined}
                                    style={{ display: "inline-flex", minWidth: 0, flex: 1 }}
                                  >
                                    <span style={{ color: "#64748b", fontSize: "0.80rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {row.phone}
                                    </span>
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className="custom-tooltip"
                            style={{ color: "#64748b", fontSize: "0.85rem", cursor: "pointer" }}
                            data-tooltip={row.education || ""}
                          >
                            {(row.education && row.education.length > 10)
                              ? `${row.education.substring(0, 10)}...`
                              : (row.education || "")}
                          </span>
                        </TableCell>
                        <TableCell>
                          <CertificationsCell certifications={row.professional_certifications} onShowAll={(title, items) => setSkillsModal({ title, items })} />
                        </TableCell>
                        <TableCell>
                          {row.resume_url ? (
                            <button
                              onClick={() => { openResumePreview(row.resume_url); }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, rgba(21, 128, 61, 0.85), rgba(22, 163, 74, 0.75))",
                                backdropFilter: "blur(8px)",
                                WebkitBackdropFilter: "blur(8px)",
                                color: "#f0fdf4",
                                border: "1px solid rgba(255, 255, 255, 0.3)",
                                boxShadow: "0 4px 10px rgba(21, 128, 61, 0.25), inset 0 1px 1px rgba(255, 255, 255, 0.4)",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                              className="custom-tooltip"
                              data-tooltip="View Resume"
                            >
                              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                              </svg>
                            </button>
                          ) : (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, rgba(148, 163, 184, 0.3), rgba(203, 213, 225, 0.2))",
                                backdropFilter: "blur(8px)",
                                WebkitBackdropFilter: "blur(8px)",
                                color: "#94a3b8",
                                border: "1px solid rgba(255, 255, 255, 0.4)",
                                boxShadow: "0 4px 10px rgba(148, 163, 184, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.5)",
                              }}
                              className="custom-tooltip"
                              data-tooltip="No resume uploaded"
                            >
                              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                              </svg>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <SkillsCell skills={row.top_10_skills} onShowAll={(title, items) => setSkillsModal({ title, items })} />
                        </TableCell>
                        <TableCell>
                          <JobCategoriesCell job_categories={row.job_categories} onShowAll={(title, items) => setSkillsModal({ title, items })} />
                        </TableCell>
                        <TableCell>
                          <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: 500 }}>
                            {row.year_of_experience !== undefined && row.year_of_experience !== null && row.year_of_experience !== "" && Number(row.year_of_experience) !== 0 ? `${Number(row.year_of_experience).toFixed(1)} Yrs` : "—"}
                          </span>
                        </TableCell>
                        <TableCell className="actions-cell">
                          <div className="actions-row">
                            {(row.status?.toLowerCase() === 'draft' || row.status?.toLowerCase() === 'action required') && (
                              row.status?.toLowerCase() === 'action required' ? (
                                <button
                                  className="action-btn custom-tooltip"
                                  style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', cursor: 'not-allowed' }}
                                  data-tooltip={`Missing fields: ${[(!row.first_name || row.first_name === 'Unknown') ? 'First Name' : null, (!row.last_name || row.last_name === 'Unknown') ? 'Last Name' : null, (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.email_address || '')) ? 'Email' : null].filter(Boolean).join(', ')}. Edit to fix.`}
                                >
                                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                                    <path d="M12 16v-4M12 8h.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  className="action-btn custom-tooltip"
                                  style={{ background: 'rgba(5, 150, 105, 0.1)', color: '#059669' }}
                                  data-tooltip="Approve"
                                  onClick={() => handleRowApprove(row)}
                                >
                                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              )
                            )}
                            <button
                              className="action-btn view custom-tooltip"
                              data-tooltip="View"
                              onClick={() =>
                                navigate(`/dashboard/learners/view`, {
                                  state: { email: row.email_address },
                                })
                              }
                            >
                              <svg
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            </button>
                            <button
                              className="action-btn delete custom-tooltip"
                              data-tooltip="Delete"
                              onClick={() => setDeleteModal(row)}
                            >
                              <svg
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {loadingMore && (
                    <TableRow>
                      <TableCell
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          padding: "16px",
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div
                            className="table-loading-spinner"
                            style={{ width: 16, height: 16 }}
                          />
                          Loading more...
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {!hasMore && state.learners.length > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        style={{
                          textAlign: "center",
                          padding: "14px",
                          color: "#cbd5e1",
                          fontSize: "0.8rem",
                        }}
                      >
                        ✓ All {state.learners.length} applicants loaded
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      {/* ── Delete Modal ── */}
      <Modal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Delete Learner"
        width="420px"
      >
        <p>{strings.learner.confirmDelete}</p>
        {deleteModal && (
          <p style={{ fontWeight: 600, marginTop: 8, color: "#0f172a" }}>
            {deleteModal.first_name} {deleteModal.last_name} (
            {deleteModal.email_address})
          </p>
        )}
        <div className="modal-actions">
          <button className="btn-cancel1" onClick={() => setDeleteModal(null)}>
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>

      {/* ── Bulk Delete Users Modal ── */}
      <Modal
        open={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title="Delete Applicants"
        width="440px"
      >
        <p style={{ color: "#475569", fontSize: "0.95rem", lineHeight: "1.5" }}>
          Are you sure you want to delete{" "}
          <strong>{selectedRows.size}</strong> selected applicant{selectedRows.size === 1 ? '' : 's'}?
          This action will delete them permanently.
        </p>
        <div className="modal-actions">
          <button className="btn-cancel1" onClick={() => setBulkDeleteModal(false)}>
            Cancel
          </button>
          <button
            className="btn-danger"
            onClick={handleBulkDeleteConfirm}
            disabled={bulkDeleting}
            style={{
              padding: "8px 16px",
              background: "#dc2626",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              fontWeight: 600,
              cursor: bulkDeleting ? "not-allowed" : "pointer",
              opacity: bulkDeleting ? 0.6 : 1
            }}
          >
            {bulkDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>

      {/* ── Note Modal ── */}
      {!!noteModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => setNoteModal(null)}>
          <div style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '460px', padding: '32px', boxShadow: '0 24px 60px rgba(0,0,0,0.15)', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#334155', margin: 0 }}>Admin Notes</h3>
              <button onClick={() => setNoteModal(null)} style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 500, whiteSpace: "pre-wrap", color: "#334155", fontSize: "0.95rem", lineHeight: 1.7 }}>
                {noteModal}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Skills/Job Categories Modal ── */}
      <Modal
        open={!!skillsModal}
        onClose={() => setSkillsModal(null)}
        title={skillsModal?.title || "Items"}
        width="500px"
      >
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          padding: "8px 4px 16px 4px",
          maxHeight: "340px",
          overflowY: "auto",
          scrollbarWidth: "thin"
        }}>
          {skillsModal?.items.map((item, i) => {
            const isSkills = skillsModal.title === "Skills";
            const isCerts = skillsModal.title === "Professional Certifications";
            const bg = isSkills ? "#e0f2fe" : isCerts ? "#f3e8ff" : "#fce7f3";
            const color = isSkills ? "#0284c7" : isCerts ? "#7e22ce" : "#be185d";
            const borderColor = isSkills ? "#bae6fd" : isCerts ? "#e9d5ff" : "#fbcfe8";
            return (
              <span key={i} style={{
                padding: "8px 16px",
                background: bg,
                color: color,
                borderRadius: "20px",
                fontSize: "0.85rem",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                border: `1px solid ${borderColor}`,
                transition: "transform 0.2s"
              }}>
                {item}
              </span>
            );
          })}
        </div>
      </Modal>

      {/* ── Approve Draft Users Modal ── */}
      <Modal
        open={approveModal}
        onClose={() => setApproveModal(false)}
        title="Approve Applicants"
        width="440px"
      >
        <p style={{ color: "#475569", fontSize: "0.95rem", lineHeight: "1.5" }}>
          Are you sure you want to approve{" "}
          <strong>{selectedDraftCount}</strong> selected applicant{selectedDraftCount === 1 ? '' : 's'}? This
          action will change their status to "active".
        </p>
        <div className="modal-actions">
          <button className="btn-cancel1" onClick={() => setApproveModal(false)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleApproveConfirm}
            disabled={approving}
          >
            {approving ? "Approving..." : "Approve"}
          </button>
        </div>
      </Modal>

      {/* ── Onboard Modal ── */}
      <Modal
        open={onboardModal}
        onClose={() => setOnboardModal(false)}
        title="Onboard Applicants"
        width="440px"
      >
        <p style={{ color: "#475569", fontSize: "0.95rem", lineHeight: "1.5" }}>
          Are you sure you want to onboard{" "}
          <strong>{selectedOnboardableCount}</strong> selected applicant{selectedOnboardableCount === 1 ? '' : 's'}? This
          will mark them as onboarded and send a welcome email with their login credentials.
        </p>
        <div className="modal-actions">
          <button className="btn-cancel1" onClick={() => setOnboardModal(false)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            style={{ background: "#2563eb", width: "fit-content", flex: "none" }}
            onClick={handleOnboardConfirm}
            disabled={onboarding}
          >
            {onboarding ? "Onboarding..." : "Onboard"}
          </button>
        </div>
      </Modal>

      {/* ── Resume PDF Viewer Modal ── */}
      {resumePreviewUrl && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '24px 80px' }}
          onClick={() => { setResumePreviewUrl(null); setPdfLoading(false); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 36, height: 36, borderRadius: '8px', background: '#dcfce7', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '15px' }}>Resume Preview</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ResumeZoomControls
                  zoom={resumeZoom}
                  onZoomChange={setResumeZoom}
                  onSearchClick={() => resumeViewerRef.current?.openSearch()}
                  onDownloadClick={async () => {
                    const downloadUrl = await getResumeDownloadUrl(resumePreviewUrl);
                    if (downloadUrl) {
                      const a = document.createElement("a");
                      a.href = downloadUrl;
                      a.download = "resume";
                      a.click();
                    }
                  }}
                />
                <button
                  onClick={() => { setResumePreviewUrl(null); setPdfLoading(false); }}
                  style={{ background: '#f1f5f9', border: 'none', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, padding: '8px 14px', borderRadius: '8px', transition: 'all 0.2s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f1f5f9'; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> Close
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ flex: 1, position: 'relative' }}>
              {pdfLoading && (
                <div style={{ position: 'absolute', inset: 0, background: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 1 }}>
                  <div style={{ width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>Loading PDF...</span>
                </div>
              )}
              <ResumeViewer
                ref={resumeViewerRef}
                key={resumePreviewUrl}
                url={resumePreviewUrl}
                zoom={resumeZoom}
                onZoomChange={setResumeZoom}
                onLoaded={() => setPdfLoading(false)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
      <Alert
        show={!!infoMessage}
        title={infoTitle}
        message={infoMessage || ""}
        type="info"
        onClose={() => setInfoMessage(null)}
      />
      <Alert
        show={!!errorMsg}
        title={errorTitle}
        message={errorMsg || ""}
        type="error"
        primaryButtonText="OK"
        onClose={() => setErrorMsg(null)}
      />
    </motion.div>
  );
};

export default LearnerListPage;
